// src/VendorAutomation.js
// Vendor Automation Component for RFQ Management within a Tender
// Handles subsystems, vendor assignment, RFQ sending, tracking, and quote comparison

import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  setDoc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';
import VendorTrackingSimple from './VendorTrackingSimple';
import QuoteUploader from './QuoteUploader';
import './styles.css';

export default function VendorAutomation({ tenderId, tender }) {
  const [activeTab, setActiveTab] = useState('subsystems'); // subsystems, tracking, comparison
  const [subsystems, setSubsystems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubsystemModal, setShowSubsystemModal] = useState(false);
  const [showVendorAssignModal, setShowVendorAssignModal] = useState(false);
  const [showQuoteUploadModal, setShowQuoteUploadModal] = useState(false);
  const [selectedSubsystem, setSelectedSubsystem] = useState(null);
  const [selectedVendorForQuote, setSelectedVendorForQuote] = useState(null);
  const [subsystemFormData, setSubsystemFormData] = useState({
    name: '',
    description: ''
  });

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [tenderId]);

  async function fetchData() {
    try {
      setLoading(true);
      await Promise.all([
        fetchSubsystems(),
        fetchVendors()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSubsystems() {
    try {
      const q = query(
        collection(db, `tenders/${tenderId}/subsystems`),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      const subsystemList = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const subsystemData = { id: docSnap.id, ...docSnap.data() };
          
          // Fetch assigned vendors for this subsystem
          const vendorsSnapshot = await getDocs(
            collection(db, `tenders/${tenderId}/subsystems/${docSnap.id}/vendors`)
          );
          subsystemData.assignedVendors = vendorsSnapshot.docs.map(v => v.id);
          
          return subsystemData;
        })
      );
      setSubsystems(subsystemList);
    } catch (error) {
      console.error('Error fetching subsystems:', error);
    }
  }

  async function fetchVendors() {
    try {
      const snapshot = await getDocs(collection(db, 'vendors'));
      const vendorList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }



  // Subsystem CRUD
  async function handleAddSubsystem(e) {
    e.preventDefault();
    
    if (!subsystemFormData.name.trim()) {
      alert('Subsystem name is required');
      return;
    }

    try {
      const subsystemData = {
        name: subsystemFormData.name.trim(),
        description: subsystemFormData.description.trim(),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(
        collection(db, `tenders/${tenderId}/subsystems`),
        subsystemData
      );

      setSubsystems([...subsystems, { id: docRef.id, ...subsystemData, assignedVendors: [] }]);
      setShowSubsystemModal(false);
      setSubsystemFormData({ name: '', description: '' });
      alert('Subsystem added successfully');
    } catch (error) {
      console.error('Error adding subsystem:', error);
      alert('Failed to add subsystem: ' + error.message);
    }
  }

  async function handleDeleteSubsystem(subsystemId) {
    if (!window.confirm('Are you sure you want to delete this subsystem?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `tenders/${tenderId}/subsystems`, subsystemId));
      setSubsystems(subsystems.filter(s => s.id !== subsystemId));
      alert('Subsystem deleted successfully');
    } catch (error) {
      console.error('Error deleting subsystem:', error);
      alert('Failed to delete subsystem: ' + error.message);
    }
  }

  // Vendor assignment
  function handleAssignVendors(subsystem) {
    setSelectedSubsystem(subsystem);
    setShowVendorAssignModal(true);
  }

  async function handleToggleVendorAssignment(vendorId) {
    if (!selectedSubsystem) return;

    try {
      const isAssigned = selectedSubsystem.assignedVendors.includes(vendorId);
      
      if (isAssigned) {
        // Remove assignment
        await deleteDoc(
          doc(db, `tenders/${tenderId}/subsystems/${selectedSubsystem.id}/vendors`, vendorId)
        );
      } else {
        // Add assignment
        await setDoc(
          doc(db, `tenders/${tenderId}/subsystems/${selectedSubsystem.id}/vendors`, vendorId),
          {
            subsystemId: selectedSubsystem.id,
            tenderId: tenderId,
            assignedAt: Timestamp.now()
          }
        );
      }

      // Update local state
      const updatedSubsystems = subsystems.map(s => {
        if (s.id === selectedSubsystem.id) {
          const updatedVendors = isAssigned
            ? s.assignedVendors.filter(v => v !== vendorId)
            : [...s.assignedVendors, vendorId];
          return { ...s, assignedVendors: updatedVendors };
        }
        return s;
      });
      
      setSubsystems(updatedSubsystems);
      
      // Update selected subsystem
      const updated = updatedSubsystems.find(s => s.id === selectedSubsystem.id);
      setSelectedSubsystem(updated);
    } catch (error) {
      console.error('Error toggling vendor assignment:', error);
      alert('Failed to update vendor assignment: ' + error.message);
    }
  }

  // State for BOQ preview modal
  const [showBOQModal, setShowBOQModal] = useState(false);
  const [boqModalData, setBOQModalData] = useState(null);

  // Send RFQ - Show BOQ table and open mailto
  async function handleSendRFQ(subsystem) {
    if (!subsystem.assignedVendors || subsystem.assignedVendors.length === 0) {
      alert('Please assign vendors to this subsystem first');
      return;
    }

    // Get vendor emails
    const vendorEmails = subsystem.assignedVendors
      .map(vendorId => {
        const vendor = vendors.find(v => v.id === vendorId);
        return vendor?.email;
      })
      .filter(email => email && email.includes('@'));

    if (vendorEmails.length === 0) {
      alert('No valid vendor emails found. Please add email addresses to vendors.');
      return;
    }

    try {
      // Fetch BOQ items for this subsystem
      const boqItemsQuery = query(
        collection(db, `tenders/${tenderId}/costingItems`),
        orderBy('createdAt', 'asc')
      );
      const boqSnapshot = await getDocs(boqItemsQuery);
      const allBOQItems = boqSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter BOQ items by subsystem
      const boqItems = allBOQItems.filter(item => 
        !item.subsystem || item.subsystem === subsystem.name || item.subsystem === subsystem.id
      );

      // Show BOQ modal with table
      setBOQModalData({
        subsystem,
        vendorEmails,
        boqItems
      });
      setShowBOQModal(true);
    } catch (error) {
      console.error('Error fetching BOQ:', error);
      alert(`Error fetching BOQ: ${error.message}`);
    }
  }

  // Open mailto with BOQ
  function openMailtoWithBOQ() {
    if (!boqModalData) return;

    const { subsystem, vendorEmails, boqItems } = boqModalData;
    const projectName = (tender.projectName || 'Project').substring(0, 100);
    const subsystemName = subsystem.name.substring(0, 100);
    const subsystemDesc = (subsystem.description || 'N/A').substring(0, 200);
    
    const subject = encodeURIComponent(`RFQ: ${projectName} - ${subsystemName}`);
    
    // Create BOQ text table
    let boqText = '';
    if (boqItems.length > 0) {
      boqText = '\n\nBill of Quantities:\n\n';
      boqItems.forEach((item, index) => {
        boqText += `${index + 1}. ${item.itemName || 'Item'}\n`;
        if (item.description) boqText += `   Description: ${item.description}\n`;
        boqText += `   Quantity: ${item.quantity || 0} ${item.unit || 'Unit'}\n`;
        if (item.make || item.model) {
          boqText += `   Make/Model: ${[item.make, item.model].filter(Boolean).join(' / ')}\n`;
        }
        if (item.remarks) boqText += `   Remarks: ${item.remarks}\n`;
        boqText += '\n';
      });
    }
    
    const body = encodeURIComponent(
      `Dear Vendor,\n\n` +
      `We would like to request a quotation for the following:\n\n` +
      `Project: ${projectName}\n` +
      `Subsystem: ${subsystemName}\n` +
      `Description: ${subsystemDesc}\n` +
      boqText +
      `Please provide your best quotation at the earliest.\n\n` +
      `Best regards`
    );

    const mailtoLink = `mailto:${vendorEmails.join(',')}?subject=${subject}&body=${body}`;
    
    // Check URL length
    if (mailtoLink.length > 2000) {
      // Fallback: simpler email without BOQ in body (user can copy from modal)
      const simpleLink = `mailto:${vendorEmails.join(',')}?subject=${subject}`;
      window.location.href = simpleLink;
      alert('Email opened with subject only. Please copy the BOQ from the table above and paste into your email.');
    } else {
      window.location.href = mailtoLink;
      setShowBOQModal(false);
    }
  }



  // Render content based on active tab
  function renderContent() {
    if (activeTab === 'subsystems') {
      return renderSubsystemsTab();
    } else if (activeTab === 'tracking') {
      return renderTrackingTab();
    }
  }

  function renderSubsystemsTab() {
    return (
      <div className="subsystems-tab">
        <div className="tab-header">
          <h3>Subsystems</h3>
          <button onClick={() => setShowSubsystemModal(true)} className="primary-button">
            + Add Subsystem
          </button>
        </div>

        {subsystems.length === 0 ? (
          <div className="empty-state">
            <p>No subsystems defined yet</p>
            <button onClick={() => setShowSubsystemModal(true)}>
              Add Your First Subsystem
            </button>
          </div>
        ) : (
          <table className="subsystems-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Assigned Vendors</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subsystems.map(subsystem => (
                <tr key={subsystem.id}>
                  <td>{subsystem.name}</td>
                  <td>{subsystem.description || '-'}</td>
                  <td>
                    {subsystem.assignedVendors?.length || 0} vendor(s)
                  </td>
                  <td>
                    <button
                      onClick={() => handleAssignVendors(subsystem)}
                      className="action-button"
                    >
                      Assign Vendors
                    </button>
                    <button
                      onClick={() => handleSendRFQ(subsystem)}
                      className="action-button"
                      disabled={!subsystem.assignedVendors || subsystem.assignedVendors.length === 0}
                    >
                      Send RFQ
                    </button>
                    <button
                      onClick={() => handleDeleteSubsystem(subsystem.id)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  function renderTrackingTab() {
    return (
      <VendorTrackingSimple 
        tenderId={tenderId}
        subsystems={subsystems}
        vendors={vendors}
        onUploadQuote={handleUploadQuote}
      />
    );
  }

  function handleUploadQuote(subsystemId, vendorId) {
    const subsystem = subsystems.find(s => s.id === subsystemId);
    setSelectedSubsystem(subsystem);
    setSelectedVendorForQuote(vendorId);
    setShowQuoteUploadModal(true);
  }

  function handleQuoteUploadSuccess() {
    setShowQuoteUploadModal(false);
    setSelectedSubsystem(null);
    setSelectedVendorForQuote(null);
    fetchData(); // Refresh data
  }



  return (
    <div className="vendor-automation-container">
      {/* Header */}
      <div className="automation-header">
        <h2>Response Tracking Dashboard</h2>
      </div>

      {/* Tabs */}
      <div className="automation-tabs">
        <button
          className={activeTab === 'subsystems' ? 'active' : ''}
          onClick={() => setActiveTab('subsystems')}
        >
          Subsystems
        </button>
        <button
          className={activeTab === 'tracking' ? 'active' : ''}
          onClick={() => setActiveTab('tracking')}
        >
          Response Tracking
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        renderContent()
      )}

      {/* Add Subsystem Modal */}
      {showSubsystemModal && (
        <div className="modal-overlay" onClick={() => setShowSubsystemModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Add Subsystem</h2>
            <form onSubmit={handleAddSubsystem}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={subsystemFormData.name}
                  onChange={e => setSubsystemFormData({ ...subsystemFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={subsystemFormData.description}
                  onChange={e => setSubsystemFormData({ ...subsystemFormData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSubsystemModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Vendors Modal */}
      {showVendorAssignModal && selectedSubsystem && (
        <div className="modal-overlay" onClick={() => setShowVendorAssignModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Assign Vendors to {selectedSubsystem.name}</h2>
            <div className="vendor-list">
              {vendors.length === 0 ? (
                <p>No vendors available. Please add vendors first.</p>
              ) : (
                vendors.map(vendor => (
                  <div key={vendor.id} className="vendor-item">
                    <input
                      type="checkbox"
                      checked={selectedSubsystem.assignedVendors.includes(vendor.id)}
                      onChange={() => handleToggleVendorAssignment(vendor.id)}
                    />
                    <span>{vendor.name} ({vendor.email})</span>
                  </div>
                ))
              )}
            </div>
            <div className="modal-actions">
              <button onClick={() => setShowVendorAssignModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Quote Upload Modal */}
      {showQuoteUploadModal && selectedSubsystem && selectedVendorForQuote && (
        <div className="modal-overlay" onClick={() => setShowQuoteUploadModal(false)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <QuoteUploader
              tenderId={tenderId}
              subsystemId={selectedSubsystem.id}
              vendorId={selectedVendorForQuote}
              onSuccess={handleQuoteUploadSuccess}
              onCancel={() => setShowQuoteUploadModal(false)}
            />
          </div>
        </div>
      )}

      {/* BOQ Preview Modal */}
      {showBOQModal && boqModalData && (
        <div className="modal-overlay" onClick={() => setShowBOQModal(false)}>
          <div className="modal-content large-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bill of Quantities - {boqModalData.subsystem.name}</h2>
              <button className="modal-close" onClick={() => setShowBOQModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Recipients: {boqModalData.vendorEmails.join(', ')}
              </p>
              
              {boqModalData.boqItems.length === 0 ? (
                <div style={{ padding: '20px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                  <p>No BOQ items found for this subsystem.</p>
                </div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <table className="boq-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ position: 'sticky', top: 0, backgroundColor: '#2c3e50', color: 'white' }}>
                      <tr>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Sr. No.</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Item Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Description</th>
                        <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Quantity</th>
                        <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd' }}>Unit</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Make/Model</th>
                        <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd' }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {boqModalData.boqItems.map((item, index) => (
                        <tr key={item.id} style={{ backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white' }}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{index + 1}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.itemName || '-'}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.description || '-'}</td>
                          <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd' }}>{item.quantity || 0}</td>
                          <td style={{ padding: '10px', textAlign: 'center', border: '1px solid #ddd' }}>{item.unit || '-'}</td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            {item.make && item.model ? `${item.make} / ${item.model}` : (item.make || item.model || '-')}
                          </td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>{item.remarks || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 600 }}>Instructions:</p>
                <ol style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                  <li>Click "Open Email Client" below to compose email</li>
                  <li>If BOQ is too large for email body, copy the table above and paste into your email</li>
                  <li>Manually send the email to vendors</li>
                  <li>After sending, update the status in Response Tracking tab</li>
                </ol>
              </div>
            </div>
            
            <div className="modal-footer">
              <button onClick={() => setShowBOQModal(false)} className="secondary-button">
                Cancel
              </button>
              <button onClick={openMailtoWithBOQ} className="primary-button">
                Open Email Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
